// Provides notification utility functions
import { unref } from "vue";
import { titleCase } from "@tod-ui/helpers/strings";
import { RESOLUTION_STATUS } from "@tod-ui/constants/task_management";

export function notificationResolution(value) {
  const notification = unref(value);
  if (!notification || !notification.name || !notification.id) {
    return { name: "Notifications" };
  }

  const triggeringEntities = decodeTriggeringEntities(
    notification.triggeringEntities
  );

  switch (notification.name) {
    case "move_in_auto_enrollment_awaiting_device":
    case "move_in_completed_without_device_and_recurring_charge":
    case "move_in_completed_with_device_set_recurring_charge":
    case "move_in_completed_without_device":
    case "move_in_completed_with_rma_device":
    case "move_out_completed_with_rma_device":
    case "move_out_completed_with_device":
    case "move_in_without_sd":
    case "pre_install":
    case "device_upgrade_immediate":
    case "device_upgrade":
      if (notification?.organization?.id && triggeringEntities?.zoneId) {
        return {
          name: "ManageSmartUnitDevices",
          params: {
            organization_id: notification.organization.id,
            zone_id: triggeringEntities.zoneId,
          },
        };
      }
      break;
    case "contact_method_failing":
      if (notification?.organization?.id && triggeringEntities.userId) {
        return {
          name: "ViewRenter",
          params: {
            organization_id: notification.organization.id,
            renter_id: triggeringEntities.userId,
          },
        };
      }
      break;
  }
  return viewNotification(notification);
}

export function decodeTriggeringEntities(entities) {
  return entities.reduce((acc, entityJson) => {
    const entity = JSON.parse(entityJson);
    switch (entity.type) {
      case "organization":
        return {
          zoneId: entity.id,
          zoneName: entity.name,
          ...acc,
        };
      case "billing_account":
        return {
          renterId: entity.id,
          renterName: entity.name,
          ...acc,
        };
      case "billing_account_organization":
        return {
          organizationId: entity.id,
          ...acc,
        };
      case "user":
        return {
          userId: entity.id,
          userFirstName: entity?.first_name,
          userLastName: entity?.last_name,
          ...acc,
        };
      case "contact_method":
        return {
          contactMethodId: entity.cm_id,
          contactMethodValue: entity.cm_value,
          contactMethodType: entity.cm_type,
          contactMethodIsDefault: entity.cm_is_default,
          ...acc,
        };
    }
    return acc;
  }, {});
}

export function viewNotification(notification) {
  return {
    name: "Notifications",
    params: { id: notification.id },
  };
}

const DISMISSABLE_NOTIFICATION_NAMES = [
  "account_locked",
  "account_unlocked",
  "move_in_completed_with_device",
  "move_out_completed_with_device",
  "move_out_completed_without_device",
  "move_out_completed_without_device_check_recurring_charge",
  "task_assigned",
  "task_due_today",
  "move_in_failed_missing_phone",
  "move_in_set_amenity_type",
];

export const TASK_NOTIFICATION_NAMES = [
  "task_assigned",
  "task_due_today",
  "task_overdue_today",
];

export function isTaskNotification(notification) {
  return TASK_NOTIFICATION_NAMES.includes(notification?.name);
}

const TERMINAL_TASK_RESOLUTIONS = [
  RESOLUTION_STATUS.DONE,
  RESOLUTION_STATUS.CANCELLED,
  "completed",
];

export function isTerminalTaskResolution(resolution) {
  return TERMINAL_TASK_RESOLUTIONS.includes(resolution);
}

export function notificationDismissible(notification) {
  const n = unref(notification);
  return (
    !n?.automatedResolution && DISMISSABLE_NOTIFICATION_NAMES.includes(n?.name)
  );
}

let documentTitleIntervalHandler = null,
  originalDocumentTitle = "";

export function restoreDocumentTitle() {
  if (documentTitleIntervalHandler) {
    clearInterval(documentTitleIntervalHandler);
    documentTitleIntervalHandler = null;
    document.title = originalDocumentTitle;
    originalDocumentTitle = "";
  }
}

export function flashDocumentTitle() {
  if (documentTitleIntervalHandler) return;

  originalDocumentTitle = document.title;
  documentTitleIntervalHandler = setInterval(() => {
    document.title =
      originalDocumentTitle === document.title
        ? "🔴 New Notification"
        : originalDocumentTitle;
  }, 1500);
}

export const NOTIFICATION_TEMPLATES = [
  {
    value: "unhealthy_device_requires_visit",
    label: "Unhealthy Device, Requires Visit",
    severity: "error",
  },
  {
    value: "unhealthy_device_with_interruption_requires_visit",
    label: "Unhealthy Device with Service Interruption, Requires Visit",
    severity: "error",
  },
  {
    value: "unhealthy_device_no_visit",
    label: "Unhealthy Device with Service Interruption, No Visit Required",
    severity: "warn",
  },
  {
    value: "temporary_service_interruption",
    label: "Temporary Service Interruption",
    severity: "warn",
  },
  {
    value: "false_negative_alert_acknowledgement",
    label: "False Negative Alert Acknowledgement",
    severity: "warn",
  },
  {
    value: "false_positive_alert_acknowledgement",
    label: "False Positive Alert Acknowledgement",
    severity: "info",
  },
  {
    value: "customer_unit_work_started",
    label: "Customer Unit, Work Started",
    severity: "info",
  },
  {
    value: "customer_unit_work_ended",
    label: "Customer Unit, Work Ended",
    severity: "info",
  },
];

export function templateLabel(name) {
  const template = NOTIFICATION_TEMPLATES.find((t) => t.value === name);
  if (template) {
    return template.label;
  }

  return titleCase(name);
}

export function templateSeverity(name) {
  const template = NOTIFICATION_TEMPLATES.find((t) => t.value === name);
  if (template) {
    return template.severity;
  }

  return "info";
}

export function severityColor(severity) {
  switch (severity) {
    case "warn":
      return "warning";
    default:
      return severity;
  }
}

export function getBulkNotificationStatusColor(status) {
  switch (status) {
    case "published":
      return "orange";
    case "resolved":
      return "green";
    case "dismissed":
      return "red";
    case "created":
      return "orange";
    default:
      return "grey";
  }
}

export function getBulkNotificationStatusText(status) {
  switch (status) {
    case "published":
      return "Pending Approval";
    case "resolved":
      return "Approved";
    case "dismissed":
      return "Rejected";
    case "created":
      return "Pending Approval";
    default:
      return titleCase(status || "");
  }
}

export function getNotificationStatusColor(status, bulkStatus) {
  // Status is based on bulk notification status, not individual notification status
  if (bulkStatus === "published") {
    return "orange";
  } else if (bulkStatus === "resolved") {
    return "green";
  } else if (bulkStatus === "dismissed") {
    return "red";
  }

  // Fallback to individual status if bulk status not available
  switch (status) {
    case "published":
      return "orange";
    case "resolved":
      return "green";
    case "dismissed":
      return "red";
    case "pending_bulk_approval":
      return "orange";
    default:
      return "grey";
  }
}

export function getNotificationStatusText(status, bulkStatus) {
  // Status is based on bulk notification status, not individual notification status
  if (bulkStatus === "published") {
    return "Pending Approval";
  } else if (bulkStatus === "resolved") {
    return "Approved";
  } else if (bulkStatus === "dismissed") {
    return "Rejected";
  }

  // Fallback to individual status if bulk status not available
  switch (status) {
    case "published":
      return "Pending Approval";
    case "resolved":
      return "Approved";
    case "dismissed":
      return "Rejected";
    case "pending_bulk_approval":
      return "Pending Bulk Approval";
    default:
      return titleCase(status || "");
  }
}
